/**
 * The fragment field — framing mode (back camera).
 *
 * Camera passthrough is a DOM <video> behind a transparent overlay of fragment
 * cards positioned by the SpatialBackend. An rAF loop reads the look direction,
 * projects each live fragment onto the screen, accrues dwell for whatever is held
 * near center, and cultivates a fragment once it has been held past threshold.
 */

import { useEffect, useRef } from 'react';
import { fragmentState, type Fragment } from '@wintermute/orchestrator';
import { runtime } from '../session/runtime.js';
import { useStore } from '../state/store.js';
import { project, isHeld } from './placement.js';
import { Iconostasis } from '../visual/Iconostasis/Iconostasis.js';
import { PointText } from '../visual/Text/PointText.js';
import { Directive } from '../visual/Directive/Directive.js';
import { viewGeometry } from '../spatial/cameraFov.js';
import { FIELD, ICONS_ENABLED } from '../config.js';
import { groundCss } from '../visual/postfx/ground.js';
import type { CameraController } from '../passthrough/useCamera.js';

interface FieldProps {
  camera: CameraController;
}

const CULTIVATE_AFTER = 3.0; // seconds held before a fragment is cultivated

export function Field({ camera }: FieldProps) {
  const live = useStore((s) => s.live);
  const seams = useStore((s) => s.seams);
  const regime = useStore((s) => s.regime);
  const ground = useStore((s) => s.ground);
  const setCameraError = useStore((s) => s.setCameraError);

  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dwellAccum = useRef<Map<string, number>>(new Map());
  const cultivated = useRef<Set<string>>(new Set());
  /** Last display state written per card, so hidden cards cost no style writes. */
  const shownRef = useRef<Map<string, boolean>>(new Map());
  const reticleRef = useRef<HTMLDivElement>(null);

  // In WebXR the compositor draws the camera itself, beneath the DOM overlay.
  // Opening getUserMedia as well would mean two consumers of the back camera —
  // which conflicts, and on some devices the second request just fails.
  const xrOwnsCamera = runtime.spatial.providesCameraPassthrough === true;

  // Ensure the back camera on mount. `start()` REUSES the stream the Rite
  // already opened (App.tsx header: "the Field inherits it still running"), so
  // entry is not a second getUserMedia; it only opens a stream when none is live
  // — notably after Reflection, which leaves the FRONT camera running.
  useEffect(() => {
    if (xrOwnsCamera) return;
    void camera.start('environment');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xrOwnsCamera]);

  // Hand the video to the runtime (VO backend motion, Watcher frames) ONLY while
  // it is genuinely showing the back camera. Coming back from Reflection,
  // `camera.facing` is still 'user' and `active` still true for one render
  // until the new stream lands; attaching then would let the Watcher upload a
  // frame of the participant's face.
  useEffect(() => {
    if (xrOwnsCamera) return;
    const backCamera = camera.active && camera.facing === 'environment';
    runtime.attachVideo(backCamera ? camera.videoRef.current : null);
  }, [camera.active, camera.facing, camera.videoRef, xrOwnsCamera]);

  // Leaving the field (Reflection, Ending): the runtime must not keep a handle
  // on a detached <video>.
  useEffect(() => () => runtime.attachVideo(null), []);

  useEffect(() => {
    setCameraError(camera.error);
  }, [camera.error, setCameraError]);

  // Per-id state outlives the fragment otherwise. A retired fragment is dead and
  // never resurfaces, so dropping its accumulators is safe.
  useEffect(() => {
    const ids = new Set(live.map((f) => f.id));
    for (const id of [...dwellAccum.current.keys()]) if (!ids.has(id)) dwellAccum.current.delete(id);
    for (const id of [...cultivated.current]) if (!ids.has(id)) cultivated.current.delete(id);
    for (const id of [...shownRef.current.keys()]) if (!ids.has(id)) shownRef.current.delete(id);
  }, [live]);

  // rAF loop: position fragments + accrue dwell.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const engine = runtime.engine;
      const cam = runtime.spatial.cameraPose();
      // Recomputed each frame: it depends on the video's intrinsic size and the
      // element's display size, either of which can change (stream negotiation,
      // rotation, resize). It is a handful of arithmetic ops.
      const view = viewGeometry(camera.videoRef.current);
      let holding = false;

      for (const [id, el] of cardRefs.current) {
        const anchor = runtime.spatial.resolve(id) ?? runtime.spatial.anchor(id);
        // Tolerate an unresolved anchor (SLAM-ready contract): hide if null-ish.
        const p = project(anchor, cam, view);
        // confidence 0 means the backend declined to pin this to the world yet —
        // the map is not trustworthy enough. Showing it anyway would put text at
        // a position we already know to be wrong.
        const pending = anchor.confidence <= 0;
        const shown = p.visible && !pending;
        // The cards are retired (invisible); retired and off-screen ones used to
        // take five style writes plus a blur every frame. Write once when a card
        // hides, then nothing until it is shown again.
        if (shown) {
          el.style.left = `${p.x}vw`;
          el.style.top = `${p.y}vh`;
          if (shownRef.current.get(id) !== true) el.style.display = 'block';
          // Depth: nearer fragments are larger and clearer; far ones recede + haze.
          el.style.transform = `translate(-50%, -50%) scale(${p.scale.toFixed(3)})`;
          // p.edge dissolves at the frame edge rather than switching off — a hard
          // cutoff made fragments flicker on every small pose correction.
          el.style.opacity = `${((1 - p.fog * 0.65) * p.edge).toFixed(3)}`;
          el.style.zIndex = String(p.zIndex);
          el.style.filter = p.fog > 0.5 ? `blur(${((p.fog - 0.5) * 2).toFixed(2)}px)` : '';
        } else if (shownRef.current.get(id) !== false) {
          el.style.display = 'none';
        }
        shownRef.current.set(id, shown);

        // A fragment that is not on screen cannot be "held" — dwell must not
        // accrue for one the backend is still withholding from the world.
        if (!pending && isHeld(p, FIELD.holdToleranceDeg)) {
          holding = true;
          if (engine) engine.dwell(id, dt);
          const acc = (dwellAccum.current.get(id) ?? 0) + dt;
          dwellAccum.current.set(id, acc);
          el.classList.add('held');
          if (acc >= CULTIVATE_AFTER && !cultivated.current.has(id)) {
            cultivated.current.add(id);
            runtime.cultivate(id);
            el.classList.add('cultivated');
          }
        } else {
          el.classList.remove('held');
        }
      }
      if (reticleRef.current) {
        reticleRef.current.classList.toggle('active', holding);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="field-root" ref={containerRef}>
      {/* Omitted entirely under WebXR — the XR compositor is the passthrough. */}
      {!xrOwnsCamera && (
        <video
          className="passthrough"
          ref={camera.videoRef}
          autoPlay
          muted
          playsInline
          // CSS only, so the tracker and the Watcher still read pristine frames.
          style={{ filter: groundCss(regime, ground) }}
        />
      )}
      {/* In immersive-ar mode the browser provides no UI chrome; participants
          need a way out that doesn't require knowing the hardware back button. */}
      {xrOwnsCamera && (
        <button className="xr-exit" onClick={() => runtime.spatial.stop()}>
          Exit AR
        </button>
      )}
      <div className="vignette" />
      {/* Incarnation layer: a cultivated fragment becomes a figure. Sits above
          the passthrough and below the cards, so a figure reads as standing in
          the room rather than pasted over the interface. */}
      {ICONS_ENABLED && <Iconostasis video={camera.videoRef.current} />}
      <div className="reticle" ref={reticleRef} />
      {/* The mapping guide is gone. It existed because anchors were withheld
          until the map proved healthy, which left the field empty for up to
          25 s. Nothing is withheld any more — anchors are placed immediately
          along a frozen ray and only their DEPTH settles afterwards, so there
          is no wait to explain. See the StoredAnchor note in AlvaARBackend. */}

      {/* Fragments are drawn as world-anchored point text, on planes committed
          to the room rather than billboarded at the lens. */}
      <PointText videoRef={camera.videoRef} />

      {/* The Watcher speaking in the air. The same line is simultaneously being
          written onto the object it named, anchored from the pose the frame was
          captured at — so the participant hears it now and finds it already
          there when they turn back. See Directive.tsx. */}
      <Directive />

      {/* The DOM cards stay mounted but are invisible (see immersion.css).
          They are no longer the fragment's appearance — PointText is — but the
          dwell loop above walks `cardRefs`, so removing them would silently
          disable attention tracking, which is the mechanic the whole artwork
          runs on. Retiring them properly means moving that loop onto `live`,
          which is a separate change with its own risk. */}
      <div className="cards cards-retired" aria-hidden="true">
        {live.map((f) => (
          <FragmentCard
            key={f.id}
            fragment={f}
            seams={seams}
            register={(el) => {
              if (el) cardRefs.current.set(f.id, el);
              else cardRefs.current.delete(f.id);
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface CardProps {
  fragment: Fragment;
  seams: boolean;
  register: (el: HTMLDivElement | null) => void;
}

function FragmentCard({ fragment, seams, register }: CardProps) {
  const state = fragmentState(fragment);
  return (
    <div ref={register} className={`frag ${state}`}>
      <span className="tag">
        {fragment.tactic}
        {fragment.mutatedBy ? ` · ${fragment.mutatedBy}` : ''}
        {seams ? ` · v${fragment.vitality.toFixed(2)} · g${fragment.generation}` : ''}
      </span>
      <span className="txt">{fragment.text}</span>
    </div>
  );
}
